/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import { OverlayTypes } from '../../../store/overlay/OverlayTypes';
import { showGuideScreen, showPreviousScreen } from '../../../store/screens/screens.reducers';
import { hideSpinner } from '../../../store/spinner/spinner.reducers';
import { showErrorOverlay } from '../../../store/videoplayer/overlays/erroroverlay/erroroverlay.reducers';
import { setIsStreamingRestricted } from '../../../store/videoplayer/streamingrestriction/streamingrestriction.reducers';
import { showErrorOSD } from '../../../store/videoplayerui/osd/osd.reducers';
import { BrandingUiInfoKeys } from '../../entities/BrandingUiInfoKeys';
import { ErrorTypes, LiveTvError } from '../../error';
import { ServicesClientImpl } from '../../interactors';
import { Locale } from '../../locale/Locale';
import { LocaleKeys } from '../../locale/LocaleKeys';
import { Log } from '../../logging';
import { PlaybackContentType } from '../../playback/enums';
import { type AppDispatch } from '../../tivo/tivo-redux-wrapper';
import { BrandingUiUtility } from '../../utils';
import { CcbSwitch } from '../../utils/ccb';
import { CcbSwitchName } from '../../utils/ccb/enums';
import { ErrorPriority, VisualDisplayTypes } from '../enums';

export class PlaybackErrorDisplay {
    private static TAG: string = 'PlaybackErrorDisplay';

    public static dispatch: AppDispatch;

    private static mPlaybackErrorDisplay: PlaybackErrorDisplay;

    private service = new ServicesClientImpl();

    public static getInstance(): PlaybackErrorDisplay {
        if (!PlaybackErrorDisplay.mPlaybackErrorDisplay) {
            PlaybackErrorDisplay.mPlaybackErrorDisplay = new PlaybackErrorDisplay();
        }
        return PlaybackErrorDisplay.mPlaybackErrorDisplay;
    }

    // eslint-disable-next-line class-methods-use-this
    public formatDescription(error: LiveTvError, playbackContentType?: PlaybackContentType) {
        const servicesClient = new ServicesClientImpl();

        const replaceableValues = {
            CUSTOMER_SUPPORT_NAME: servicesClient.getBrandingUiInfoForKey(
                BrandingUiInfoKeys.CUSTOMER_SUPPORT_NAME,
            ),
            DEVICE_NAME: servicesClient.getBrandingUiInfoForKey(BrandingUiInfoKeys.DEVICE_NAME),
            DEVICE_NAME_SHORT: servicesClient.getBrandingUiInfoForKey(
                BrandingUiInfoKeys.DEVICE_NAME_SHORT,
            ),
            DISPLAY_NAME: servicesClient.getBrandingUiInfoForKey(BrandingUiInfoKeys.DISPLAY_NAME),
            DISTRIBUTOR_NAME: servicesClient.getBrandingUiInfoForKey(
                BrandingUiInfoKeys.DISTRIBUTOR_NAME,
            ),
            EPG_LOOK_BACK_HOURS: servicesClient.getBrandingUiInfoForKey(
                BrandingUiInfoKeys.EPG_LOOK_BACK_HOURS,
            ),
            MANUFACTURER_NAME: servicesClient.getBrandingUiInfoForKey(
                BrandingUiInfoKeys.MANUFACTURER_NAME,
            ),
            PHONE_CUSTOMER_SUPPORT_NUMBER: servicesClient.getBrandingUiInfoForKey(
                BrandingUiInfoKeys.PHONE_CUSTOMER_SUPPORT_NUMBER,
            ),
            START_OVER: servicesClient.getBrandingUiInfoForKey(BrandingUiInfoKeys.START_OVER),
            URL_CUSTOMER_SUPPORT: servicesClient.getBrandingUiInfoForKey(
                BrandingUiInfoKeys.URL_CUSTOMER_SUPPORT,
            ),
            URL_ERROR: servicesClient.getBrandingUiInfoForKey(BrandingUiInfoKeys.URL_ERROR),
            URL_PRIVACY: servicesClient.getBrandingUiInfoForKey(BrandingUiInfoKeys.URL_PRIVACY),
            YOUR_CABLE_PROVIDER: servicesClient.getBrandingUiInfoForKey(
                BrandingUiInfoKeys.YOUR_CABLE_PROVIDER,
            ),
            PLAYBACK_TYPE: (() => {
                switch (playbackContentType) {
                    case PlaybackContentType.STARTOVER_BEGINNING:
                    case PlaybackContentType.STARTOVER_RESUME:
                        return servicesClient.getBrandingUiInfoForKey(
                            BrandingUiInfoKeys.START_OVER,
                        );
                    case PlaybackContentType.CATCHUP_BEGINNING:
                    case PlaybackContentType.CATCHUP_RESUME:
                        return servicesClient.getBrandingUiInfoForKey(BrandingUiInfoKeys.CATCH_UP);
                    case PlaybackContentType.NDVR_LIVE:
                    case PlaybackContentType.NDVR_RECORDED:
                        return Locale.getString(
                            LocaleKeys.LIVE_TV_STANDARD_INFO_BANNER_RECORDING_STRING,
                        );
                    default:
                        return Locale.getString(
                            LocaleKeys.LIVE_TV_STANDARD_INFO_BANNER_LIVE_TV_STRING,
                        );
                }
            })(),
        };

        error.setDescription(
            BrandingUiUtility.formatDescription(error.getDescription(), replaceableValues),
        );
    }

    // eslint-disable-next-line class-methods-use-this
    private onOverlayAutoHide(): void {
        PlaybackErrorDisplay.dispatch(showPreviousScreen());
    }

    private showOsd = (error: LiveTvError, priority: number) => {
        PlaybackErrorDisplay.dispatch(
            showErrorOSD(error.getCode(), error.getDescription(), priority),
        );
    };

    private showOverlay = (error: LiveTvError) => {
        PlaybackErrorDisplay.dispatch(
            showErrorOverlay({
                errorPriority: ErrorPriority.SESSION_AND_PLAYER_ERROR,
                title: error.getTitle(),
                options: [
                    {
                        text: Locale.getString(LocaleKeys.OVERLAY_OPTION_OK_TEXT),
                    },
                ],
                messageCode: error.getCode(),
                description: error.getDescription(),
                hasDimmer: false,
                hasSeperator: false,
                overlayType: OverlayTypes.ERROR,
                onAutoHide: this.onOverlayAutoHide,
            }),
        );
    };

    // TODO: [KST-5305] Remove this method
    public showVisualDisplay = (error: any) => {
        switch (error.getVisualDisplayType()) {
            case VisualDisplayTypes.OVERLAY:
                PlaybackErrorDisplay.dispatch(hideSpinner());
                PlaybackErrorDisplay.dispatch(
                    showErrorOverlay({
                        errorPriority: error.getPriority(),
                        title: error.getTitle(),
                        options: [
                            {
                                text: Locale.getString(LocaleKeys.OVERLAY_OPTION_OK_TEXT),
                            },
                        ],
                        messageCode: error.getUiErrorCode(),
                        description: error.getDescription(),
                        hasDimmer: false,
                        hasSeperator: false,
                        overlayType: OverlayTypes.ERROR,
                        onAutoHide: this.onOverlayAutoHide,
                    }),
                );
                break;
            case VisualDisplayTypes.OSD:
                PlaybackErrorDisplay.dispatch(
                    showErrorOSD(
                        error.getUiErrorCode(),
                        error.getDescription(),
                        error.getPriority(),
                    ),
                );
                break;
            case VisualDisplayTypes.SPINNER:
                break;
            default:
        }
    };

    public setIsStreamingRestricted = (isStreamingRestricted: boolean) => {
        Diagnostics.debug(
            PlaybackErrorDisplay.TAG,
            'setIsStreamingRestricted: ',
            isStreamingRestricted,
        );
        Log.d(PlaybackErrorDisplay.TAG, 'setIsStreamingRestricted: ', isStreamingRestricted);
        PlaybackErrorDisplay.dispatch(setIsStreamingRestricted(isStreamingRestricted));
    };

    public displayError = (error: LiveTvError, playbackContentType?: PlaybackContentType) => {
        this.formatDescription(error, playbackContentType);
        PlaybackErrorDisplay.dispatch(hideSpinner());

        const overlayErrors = new Set([
            ErrorTypes.LIVE_TV_SHAKA_PLAYER_ADS_ERROR_V960,
            ErrorTypes.LIVE_TV_SHAKA_PLAYER_CAST_ERROR_V958,
            ErrorTypes.LIVE_TV_SHAKA_PLAYER_DRM_ERROR_V956,
            ErrorTypes.LIVE_TV_SHAKA_PLAYER_MANIFEST_ERROR_V954,
            ErrorTypes.LIVE_TV_SHAKA_PLAYER_MEDIA_ERROR_V953,
            ErrorTypes.LIVE_TV_SHAKA_PLAYER_NETWORK_ERROR_V951,
            ErrorTypes.LIVE_TV_SHAKA_PLAYER_PLAYER_ERROR_V957,
            ErrorTypes.LIVE_TV_SHAKA_PLAYER_STORAGE_ERROR_V959,
            ErrorTypes.LIVE_TV_SHAKA_PLAYER_STREAMING_MEDIA_V955,
            ErrorTypes.LIVE_TV_SHAKA_PLAYER_TEXT_ERROR_V952,
            ErrorTypes.LIVE_TV_SHAKA_PLAYER_ENFORCEMENT_ERROR_V948,
            ErrorTypes.LIVE_TV_AIRING_DATA_FAILED_V139,
            ErrorTypes.LIVE_TV_DEVICE_ERROR_MISSING_CONFIG_V904,
            ErrorTypes.LIVE_TV_GRACE_PERIOD_ENDED_ERROR_C718,
            ErrorTypes.LIVE_TV_NOT_READY_FOR_PLAYBACK_V117,
            ErrorTypes.LIVE_TV_PLAYLIST_URI_MISSING_V908,
            ErrorTypes.LIVE_TV_VPCONFIGURATIONS_EMPTY_RESPONSE_ERROR_V934,
            ErrorTypes.LIVE_TV_VPCONFIGURATIONS_MISCONFIGURATION_ERROR_V909,
            ErrorTypes.LIVE_TV_SESSION_ERROR_AUTHENTICATION_EXPIRED_ERROR_V409,
            ErrorTypes.LIVE_TV_SESSION_ERROR_BAD_ARGUMENT_V137,
            ErrorTypes.LIVE_TV_SESSION_ERROR_INTERNAL_ERROR_V138,
            ErrorTypes.LIVE_TV_SESSION_ERROR_NO_SESSION_V922,
            ErrorTypes.LIVE_TV_SESSION_ERROR_NOT_AUTHENTICATED_V403,
            ErrorTypes.LIVE_TV_SESSION_ERROR_NOT_AUTHORIZED_V410,
            ErrorTypes.LIVE_TV_SESSION_ERROR_NOT_REGISTERED_V406,
            ErrorTypes.LIVE_TV_SESSION_ERROR_TOO_MANY_STREAMS_V407,
            ErrorTypes.LIVE_TV_SESSION_ERROR_TOO_MANY_STREAMS_FOR_RESOURCE_ERROR_V408,
            ErrorTypes.LIVE_TV_SESSION_ERROR_UNKNOWN_ERROR_V603,
            ErrorTypes.LIVE_TV_SHAKA_PLAYER_UNKNOWN_ERROR_V999,
            ErrorTypes.LIVE_TV_UNSUPPORTED_CONTENT_ERROR_V950,
        ]);

        const osdErrors = new Map([
            [ErrorTypes.LIVE_TV_IN_BLACKLIST_OSD_ERROR_C138, ErrorPriority.GENERAL_RESTRICTIONS],
            [
                ErrorTypes.LIVE_TV_SESSION_ERROR_NOT_AUTHORIZED_DEVICE_TYPE_V414,
                ErrorPriority.GENERAL_RESTRICTIONS,
            ],
            [ErrorTypes.LIVE_TV_OOH_OSD_ERROR_V404, ErrorPriority.GENERAL_RESTRICTIONS],
            [ErrorTypes.LIVE_TV_OUT_OF_REGION_OSD_ERROR_V405, ErrorPriority.GENERAL_RESTRICTIONS],
            [
                ErrorTypes.LIVE_TV_CHANNEL_NOT_SUBSCRIBED_ERROR_CODE_V56,
                ErrorPriority.GENERAL_RESTRICTIONS,
            ],
            [
                ErrorTypes.LIVE_TV_LOCATION_RESTRICTION_OSD_ERROR_CODE_V418,
                ErrorPriority.LOCATION_RESTRICTION,
            ],
            [ErrorTypes.NO_NETWORK_OVERLAY_ERROR_CODE, ErrorPriority.NO_NETWORK_ERROR],
        ]);

        const errorCode = error.getCode().split(LiveTvError.separator)[0];
        if (overlayErrors.has(errorCode)) {
            this.showOverlay(error);
        } else if (osdErrors.has(errorCode)) {
            const priority = osdErrors.get(errorCode);
            if (priority) this.showOsd(error, priority);
        } else {
            this.showOsd(error, ErrorPriority.GENERAL_RESTRICTIONS);
        }
    };
}
